import { forwardRef } from 'react';
import { useGameStore } from '../../store/game-store';
import Piece from './Piece';
import type { ColorPair } from './piece-themes';

interface DraggedPieceProps {
  /** Side length of a board square in SVG units (board's SQUARE_SIZE). */
  squareSize: number;
  /** Lato della scacchiera in unità SVG: è il `viewBox` che il layer deve condividere. */
  boardPixelSize: number;
  flipped: boolean;
  colorPair: ColorPair;
}

/**
 * La pedina che galleggia sopra la scacchiera mentre la si trascina.
 *
 * ═══ SI DISEGNA UNA VOLTA SOLA, POI SI SPOSTA ═══════════════════════════════════
 *
 * Questo componente NON si sottoscrive a `dragPosition`. Prima lo faceva — era l'unico —
 * e sembrava la scelta giusta: un frame di trascinamento ri-renderizzava un solo `<Piece>`
 * invece dei cento `<rect>` della scacchiera. Ma «un solo `<Piece>`» non è poco:
 * `Piece` è millenovecento righe, non ha un `useMemo`, e ricostruisce a ogni chiamata
 * ventidue path — contorni lisci in Catmull-Rom, solchi, labbro, parete — più tutto il
 * proprio `<defs>`, filtro d'ombra compreso. Sessanta volte al secondo, su un telefono
 * del 2017, è il lavoro che fa restare la pedina indietro rispetto al dito.
 *
 * Ora il pezzo si disegna una volta alla presa, ALL'ORIGINE CONGELATA, e a muoverlo è
 * `GameBoard`, che scrive un `transform` sul `<g>` qui sotto direttamente nel gestore del
 * puntatore. Per frame: un attributo, zero render, zero `set` nello store.
 *
 * L'ORIGINE CONGELATA è `dragState.offsetX/offsetY`, cioè il punto in cui il dito ha preso
 * la pedina — e non il centro della casella, perché è là che la pedina salta alla presa.
 * Quei due numeri sono scritti da `startDrag` e NON cambiano per tutta la durata della
 * presa: solo `dragPosition` cambiava. È la proprietà che rende lecito congelare.
 *
 * ═══ ED È UN `<svg>` A SÉ, NON UN `<g>` DENTRO LA SCACCHIERA ════════════════════
 *
 * Congelare il disegno toglie il JavaScript, ma non basta: finché la pedina vive nel layer
 * della scacchiera, spostarla invalida quel layer e le tessere sotto di lei si
 * ri-rasterizzano comunque — con le quattro gaussiane che il pezzo si porta dietro (le due
 * `feDropShadow` del suo `<filter>` più i `drop-shadow()` CSS). Misurato sul profilo di un
 * telefono del 2017: il movimento costava 6480 ms di CPU su tre secondi, di cui soli 331
 * di JavaScript. Il grosso stava dopo il JavaScript.
 *
 * In Blink un `<g>` dentro un `<svg>` non può avere un layer proprio: i figli di un SVG
 * sono dipinti nel layer della radice. Un `<svg>` RADICE invece è un elemento con
 * `PaintLayer`, e `will-change: transform` lo promuove. Su un layer composito il disegno
 * si rasterizza UNA VOLTA, alla presa, e ogni frame successivo è uno spostamento di quad
 * sulla GPU.
 *
 * Stesso `viewBox` della scacchiera = stesse coordinate: `<Piece>` si disegna con i numeri
 * di sempre e non c'è nessuna conversione da sbagliare. Il ribaltamento prende la stessa
 * `rotate(180deg)`, e la traslazione si compone dentro quel frame.
 *
 * ═══ ED È LARGO TRE CASELLE, NON QUANTO LA SCACCHIERA ═══════════════════════════
 *
 * Un layer composito è una texture, e il compositore la ricompone per intero a ogni
 * frame in cui si sposta. Grande quanto la scacchiera erano 1002×1002 pixel di device a
 * DPR 3 — quattro megabyte — mossi sessanta volte al secondo per disegnare una pedina da
 * settantotto. Il costo cresce con la VELOCITÀ del dito, perché più il layer si sposta
 * più ampia è la regione di schermo da ricomporre: è esattamente il sintomo riferito, un
 * trascinamento lento fluido e uno veloce ancora a scatti.
 *
 * Tre caselle sono ~1/120 dell'area, e lasciano mezza casella di margine per lato:
 * abbastanza per l'ombra e per l'alone verde, che escono dal riquadro del pezzo.
 *
 * A scacchiera girata il riquadro va messo dove il pezzo si vede DAVVERO, cioè al punto
 * specchiato: le coordinate congelate sono nello spazio non ruotato della scacchiera, ed
 * è la `rotate(180deg)` del foglio a portarle a schermo. La rotazione del layer avviene
 * attorno al proprio centro, che è il centro del pezzo — e lì si annulla esattamente con
 * la contro-rotazione che `Piece` si applica da sé.
 *
 * Il layer si monta solo durante la presa: un `will-change` permanente è un layer
 * permanente in memoria.
 *
 * `overflow: visible` perché l'ombra e l'alone escono dal riquadro del pezzo; a tagliarli
 * resta il `.game-board-container`, come prima.
 *
 * Il `<Piece>` non sta sul nodo che si trasla: `.piece` ha `transition: transform 0.15s`
 * (Piece.css), che sullo stesso nodo inseguirebbe il dito con un ritardo di un sesto di
 * secondo.
 */
const DraggedPiece = forwardRef<SVGSVGElement, DraggedPieceProps>(
  function DraggedPiece({ squareSize, boardPixelSize, flipped, colorPair }, ref) {
    const dragState = useGameStore((state) => state.dragState);
    const captureInProgress = useGameStore((state) => state.captureInProgress);
    const pieces = useGameStore((state) => state.pieces);

    if (!dragState) return null;

    // During a capture in progress, the dragged piece is the one at originalFrom,
    // not dragState.square (which tracks the current hop).
    const draggedPiece = captureInProgress
      ? pieces.find((p) => p.square === captureInProgress.originalFrom)
      : pieces.find((p) => p.square === dragState.square);

    if (!draggedPiece) return null;

    /** Lato del riquadro, in unità SVG: il pezzo più mezza casella di margine per lato. */
    const lato = squareSize * 3;
    const { offsetX, offsetY } = dragState;
    // Dove il riquadro si posa nel contenitore: al punto specchiato se la scacchiera è
    // girata, perché è là che il pezzo si vede.
    const postoX = flipped ? boardPixelSize - offsetX : offsetX;
    const postoY = flipped ? boardPixelSize - offsetY : offsetY;
    const perCento = (v: number) => `${(v / boardPixelSize) * 100}%`;

    return (
      <svg
        ref={ref}
        className={`game-board-drag-layer${flipped ? ' flipped' : ''}`}
        // Il `viewBox` inquadra il pezzo alle sue coordinate di sempre: `Piece` continua a
        // disegnarsi con i numeri della scacchiera e non c'è nessuna conversione.
        viewBox={`${offsetX - lato / 2} ${offsetY - lato / 2} ${lato} ${lato}`}
        style={{
          left: perCento(postoX - lato / 2),
          top: perCento(postoY - lato / 2),
          width: perCento(lato),
          height: perCento(lato),
        }}
        aria-hidden="true"
      >
        <Piece
          key={`dragging-${draggedPiece.square}`}
          piece={draggedPiece}
          x={dragState.offsetX}
          y={dragState.offsetY}
          size={squareSize * 0.8}
          isSelected={true}
          isDragging={true}
          // In transit through a multi-capture: render as ghost like the static hop.
          ghost={!!captureInProgress}
          flipped={flipped}
          colorPair={colorPair}
        />
      </svg>
    );
  },
);

export default DraggedPiece;
